import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import IncomingCallDialog from './IncomingCallDialog';
import CallSurface from './CallSurface';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * GlobalCallOverlay
 *
 * Mounted once per layout (StaffLayout & CustomerLayout) so incoming calls,
 * outgoing calls and active call UI are visible from EVERY page in the app —
 * not only the Chat page.
 *
 * Responsibilities:
 *  1. Render <IncomingCallDialog/> when callState.phase === 'incoming_ringing'.
 *  2. Render <VideoCall/> when an outgoing/connecting/connected call is active.
 *  3. Process notification deep-links of the form
 *     `?incoming_call=<callId>&caller=<name>&call_type=<voice|video>&auto_answer=true`.
 *     Validates the call against the backend BEFORE showing any UI — if the
 *     call is already ended/missed/rejected the URL params are cleared and
 *     a toast is shown ("Missed call from X").  This prevents the
 *     persistent ghost dialog the user reported.
 *  4. Persist the active incoming call to sessionStorage so the Login page's
 *     "tap to log in & answer" banner can pick it up if the user has been
 *     auto-logged-out mid-call.
 */
export default function GlobalCallOverlay() {
  const chat = useChat();
  const { token } = useAuth();
  const callState = chat.callState;
  const location = useLocation();
  const navigate = useNavigate();
  // Ring timeout from admin settings + "call me back" notes (pending ones pop up once at login)
  useEffect(() => {
    if (!token) return undefined;
    const h = { Authorization: `Bearer ${token}` };
    fetch(`${API}/api/calls/media-config`, { headers: h }).then((r) => (r.ok ? r.json() : null)).then((c) => { if (c && c.no_answer_seconds) window.__bbcNoAnswerSeconds = c.no_answer_seconds; }).catch(() => {});
    fetch(`${API}/api/calls/callback-notes/pending`, { headers: h }).then((r) => (r.ok ? r.json() : [])).then((notes) => {
      if (!Array.isArray(notes) || !notes.length || window.__bbcCbNotesShown) return;
      window.__bbcCbNotesShown = true;
      notes.forEach((n) => toast(`${n.from_name} asked you to call back`, { description: n.note || 'They tried to call you while you were signed out.', duration: 20000,
        action: { label: 'Call back now', onClick: () => {
          const conv = n.conversation_id || ((chat.conversations || []).find((c) => c.type === 'direct' && (c.participants || []).includes(n.from_id)) || {}).conversation_id;
          if (conv && chat.initiateCall) { navigate(`/staff/chat?conversation=${conv}`); setTimeout(() => chat.initiateCall(conv, n.call_type || 'audio'), 600); }
          else navigate('/staff/chat');
        } } }));
      fetch(`${API}/api/calls/callback-notes/seen`, { method: 'POST', headers: h }).catch(() => {});
    }).catch(() => {});
    const offer = async (e) => {
      const d = e.detail || {};
      const { promptDialog } = await import('../../hooks/useConfirm');
      const note = await promptDialog({ title: `Leave a "call me back" note for ${d.name || 'them'}?`, description: 'They will see it as soon as they sign in to the BBC app.', label: 'Message (optional)', placeholder: 'e.g. Please call me back about today\'s collections', required: false });
      if (note === null || note === undefined || note === false) return;
      fetch(`${API}/api/calls/callback-notes`, { method: 'POST', headers: { ...h, 'Content-Type': 'application/json' }, body: JSON.stringify({ recipient_id: d.recipient_id, conversation_id: d.conversation_id || null, call_type: d.call_type || 'audio', note: String(note || '') }) })
        .then((r) => (r.ok ? toast.success('Call-back note left') : toast.error('Could not save the note'))).catch(() => toast.error('Could not save the note'));
    };
    window.addEventListener('bbc:offer-callback-note', offer);
    return () => window.removeEventListener('bbc:offer-callback-note', offer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [autoAnswer, setAutoAnswer] = useState(false);
  const processedDeepLinkRef = useRef(null);

  // -------------------------------------------------------------------
  // 0. Listen for NAVIGATE messages from the Service Worker.
  //    When the user taps an incoming-call push notification while the
  //    app is MINIMISED, the browser/Android wakes the existing window
  //    (via WindowClient.focus()) but `WindowClient.navigate()` is
  //    unreliable on TWA / Android WebView — the URL never changes and
  //    the call dialog never appears.  The SW now also postMessage's the
  //    target path to the SPA; we navigate via React Router here, which
  //    always works inside the app.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onSwMessage = (event) => {
      const data = event && event.data;
      if (!data || data.type !== 'NAVIGATE' || !data.path) return;
      try {
        // Use React Router so we don't reload the bundle.  Strip the
        // origin prefix if present (`navigate` expects a relative path).
        const path = data.path.startsWith('http')
          ? new URL(data.path).pathname + new URL(data.path).search
          : data.path;
        navigate(path);
      } catch (_) { /* noop */ }
    };
    navigator.serviceWorker.addEventListener('message', onSwMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onSwMessage);
  }, [navigate]);

  // -------------------------------------------------------------------
  // 1. Persist active incoming-call info for the Login banner ("4b").
  //    When the call goes idle, clear it.
  // -------------------------------------------------------------------
  useEffect(() => {
    try {
      if (callState.phase === 'incoming_ringing' && callState.callId) {
        sessionStorage.setItem('bbc_active_incoming_call', JSON.stringify({
          callId: callState.callId,
          callType: callState.callType,
          callerName: callState.callerName,
          callerId: callState.callerId,
          conversationId: callState.conversationId,
          ts: Date.now(),
        }));
      } else if (callState.phase === 'idle') {
        sessionStorage.removeItem('bbc_active_incoming_call');
      }
    } catch (_) { /* quota — ignore */ }
  }, [callState.phase, callState.callId, callState.callType, callState.callerName, callState.callerId, callState.conversationId]);

  // -------------------------------------------------------------------
  // 2. Process `?incoming_call=...` deep-links.  Validate the call against
  //    the backend BEFORE showing any dialog.  If the call is already
  //    ended/missed, clear the URL and show a toast — no ghost dialog.
  // -------------------------------------------------------------------
  useEffect(() => {
    const incomingCallId = searchParams.get('incoming_call');
    const callerName = searchParams.get('caller');
    if (!incomingCallId || !callerName || !token) return;
    // De-dupe — don't re-process the same callId twice
    if (processedDeepLinkRef.current === incomingCallId) return;
    processedDeepLinkRef.current = incomingCallId;

    const wantsAutoAnswer = searchParams.get('auto_answer') === 'true';
    const callTypeParam = searchParams.get('call_type') || 'video';
    const conversationIdParam = searchParams.get('conversation_id') || null;
    const decodedCallerName = decodeURIComponent(callerName || '');

    // Strip the call params from the URL immediately so a refresh/back
    // doesn't re-trigger the processing.  Keep any non-call params.
    const next = new URLSearchParams(searchParams);
    ['incoming_call', 'caller', 'call_type', 'conversation_id', 'auto_answer'].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });

    // ────────────────────────────────────────────────────────────────
    // STEP 1 — INJECT IMMEDIATELY from URL params.
    //
    // Why before REST?  On cold-start (app force-closed, then push
    // tapped) the user is staring at a freshly-booted SPA.  Adding a
    // network round-trip before the dialog appears means a 1-3 s
    // dead window where:
    //   • The caller's outgoing-ringing timer keeps counting down.
    //   • The user taps the push, sees an empty chat page, panics,
    //     swipes away.
    //   • By the time REST resolves, the receiver may have backgrounded
    //     the app or the caller may have hung up.
    //
    // We trust the URL params because they came from the FCM payload
    // we ourselves built server-side.  Any forged/expired call_id is
    // self-correcting: STEP 2 below will dismiss the dialog if the
    // backend confirms the call is no longer ringing.
    // ────────────────────────────────────────────────────────────────
    if (wantsAutoAnswer) setAutoAnswer(true);
    if (chat.callState.phase === 'idle' && chat._handleCallEvent) {
      chat._handleCallEvent({
        type: 'incoming_call',
        call_id: incomingCallId,
        caller_id: null,                  // unknown until REST resolves
        caller_name: decodedCallerName,
        call_type: callTypeParam,
        conversation_id: conversationIdParam,
      });
    }

    // ────────────────────────────────────────────────────────────────
    // STEP 2 — Validate against the backend in parallel.
    //
    // If the call is no longer ringing (caller hung up before we got
    // here, or another device already accepted), gracefully dismiss
    // the dialog and surface the right toast.  If still ringing,
    // patch in the authoritative caller_id from the response so
    // call_accept / WebRTC signalling has the right peer.
    // ────────────────────────────────────────────────────────────────
    fetch(`${API}/api/calls/${incomingCallId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        const status = data.status;
        if (status === 'ringing' || status === 'pending') {
          // Healthy — leave the dialog up; record which media engine the call uses.
          if (data.media_mode && chat.setMediaMode) chat.setMediaMode(data.media_mode);
          return;
        }
        if (status === 'ongoing') {
          toast.info('Call already in progress', {
            description: 'You answered from another device.',
          });
          // Don't tear down — useCallManager will sync via its own
          // poller / WS event.
          return;
        }
        // Ended / missed / rejected — dismiss the prematurely-shown
        // dialog and inform the user.
        if (chat.rejectCall) {
          // rejectCall() resets phase → idle and stops ringtone.
          // It also sends call_reject which is a no-op for already-
          // ended calls server-side.
          try { chat.rejectCall(); } catch (_) { /* noop */ }
        }
        toast(`Missed call from ${decodedCallerName}`, {
          icon: '\uD83D\uDCDE',
          description: 'The call ended before you could answer.',
        });
      })
      .catch(() => { /* network — fail-quiet, leave dialog as-is */ });
  }, [searchParams, setSearchParams, token, chat]);

  // -------------------------------------------------------------------
  // 3. Auto-clear autoAnswer when the call leaves the ringing phase, so
  //    the next manual incoming call doesn't accidentally inherit it.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (callState.phase !== 'incoming_ringing' && autoAnswer) setAutoAnswer(false);
  }, [callState.phase, autoAnswer]);

  // -------------------------------------------------------------------
  // 4. When user navigates to Chat manually while a call is connected,
  //    don't double-render — the global overlay handles it.  Other pages
  //    just see the floating overlay over their content.
  // -------------------------------------------------------------------
  // (Nothing to do here — old per-page mounts removed in Chat.js)

  // -------------------------------------------------------------------
  // 5. Native-app cold-start fallback.  When the app is launched fresh
  //    from a tap on an FCM push (Android TWA), the launcher activity
  //    opens at the root URL — no `?incoming_call` deep link, no
  //    WebSocket connected yet to receive the original ringing event.
  //    On WS reconnect the backend replays in-flight calls, so the
  //    primary recovery path is the WS replay.  This polling is a
  //    BELT-AND-BRACES backup that ONLY fires while the WS is offline,
  //    so it never races against the replay (which would cause
  //    duplicate `incoming_call` events to be processed).
  // -------------------------------------------------------------------
  // Latest chat context for the stable poll closure below (the effect intentionally has no deps, so
  // reading `chat` directly would freeze isConnected=false from the first render and poll every 3 s forever).
  const chatRef = useRef(chat);
  chatRef.current = chat;
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    let intervalId = null;

    const poll = async () => {
      if (cancelled) return;
      const live = chatRef.current;
      // Skip if a call is already in flight
      if (live.callState && live.callState.phase !== 'idle') return;
      // Skip while the WS is healthy — the WS reconnect-replay is the
      // primary recovery path and is more responsive than polling.
      if (live.isConnected) return;
      try {
        const res = await fetch(`${API}/api/calls/pending-incoming`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!Array.isArray(data) || data.length === 0) return;
        const c = data[0]; // Most recent
        if (chatRef.current._handleCallEvent) {
          chatRef.current._handleCallEvent({
            type: 'incoming_call',
            call_id: c.call_id,
            caller_id: c.caller_id,
            caller_name: c.caller_name,
            call_type: c.call_type,
            media_mode: c.media_mode,
            conversation_id: c.conversation_id,
          });
        }
      } catch (_) { /* network — quiet */ }
    };

    // Run once on mount, then every 3 s while the tab is visible AND
    // the WS is offline.  3s (down from 15s) gives a tight cold-start
    // safety net — when the app is launched fresh from a push tap and
    // the WS hasn't connected yet, we surface the ringing call within
    // a couple of seconds instead of waiting up to 15.  The poll itself
    // bails immediately when the WS reconnects so this isn't extra
    // load during normal operation.  Pauses while hidden to save battery.
    poll();
    const tick = () => { if (document.visibilityState === 'visible') poll(); };
    intervalId = setInterval(tick, 3000);

    const onVisible = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // chat.callState/.isConnected referenced inside via closure but not
    // as deps — we intentionally keep this effect stable so the
    // interval survives every callState transition.  The poll function
    // itself reads the latest values on each tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // -------------------------------------------------------------------
  // 6. Mid-call WS drop → "Reconnecting…" toast.
  //    If the WS disconnects while we're in any active call phase,
  //    show a persistent toast with a spinner so the user knows the
  //    app is fighting the network and shouldn't force-quit.  Toast
  //    is dismissed automatically when the WS reconnects.  No toast
  //    is shown for `idle` calls (just normal browsing).
  // -------------------------------------------------------------------
  const reconnectToastIdRef = useRef(null);
  useEffect(() => {
    const inActiveCall = chat.callState && chat.callState.phase !== 'idle';
    if (inActiveCall && !chat.isConnected) {
      // Dropped mid-call — show the toast (persistent until reconnect)
      if (!reconnectToastIdRef.current) {
        reconnectToastIdRef.current = toast.loading('Reconnecting…', {
          description: 'Trying to restore your call connection',
          duration: Infinity,
          id: 'call-reconnecting',
        });
      }
    } else if (reconnectToastIdRef.current) {
      // Either the call ended OR the WS came back — dismiss
      toast.dismiss(reconnectToastIdRef.current);
      reconnectToastIdRef.current = null;
      // Only show "Reconnected" if we ARE still in a call (i.e. WS
      // came back, call survived).  Skip if call has already gone idle.
      if (inActiveCall && chat.isConnected) {
        toast.success('Reconnected', { duration: 2000 });
      }
    }
  }, [chat.isConnected, chat.callState.phase]);

  // Suppress unused vars warning (location reserved for future use)
  void location; void navigate;

  return (
    <>
      {callState.phase === 'incoming_ringing' && (
        <IncomingCallDialog autoAnswer={autoAnswer} />
      )}
      {(callState.phase === 'connecting' || callState.phase === 'connected' || callState.phase === 'outgoing_ringing') && (
        <CallSurface />
      )}
    </>
  );
}
